/**
 * Utility for handling Office.js initialization
 * Provides a centralized way to ensure Office.js is properly initialized
 * before attempting to use Office APIs
 */

import { nrPageAction } from './newrelic';

// Initialization state
let isOfficeInitialized = false;
let isInitializing = false;
let initializationPromise: Promise<boolean> | null = null;
let initializationError: Error | null = null;

// Callbacks to run when Office is initialized
const initCallbacks: (() => void)[] = [];

/**
 * Initializes Office.js and returns a promise that resolves when initialization is complete
 * This should be called as early as possible in your application
 */
export const initializeOffice = (): Promise<boolean> => {
  // If already initialized, return resolved promise
  if (isOfficeInitialized) {
    return Promise.resolve(true);
  }

  // If already initializing, return the existing promise
  if (initializationPromise) {
    return initializationPromise;
  }

  // Start initialization
  isInitializing = true;
  initializationPromise = new Promise<boolean>((resolve, reject) => {
    try {
      // Check if Office exists in global scope
      if (!global.Office) {
        const error = new Error('Office.js is not available in this environment');
        nrPageAction('officeInitialization', { message: error.message });
        initializationError = error;
        isInitializing = false;
        reject(error);
        return;
      }

      // Use Office.onReady to wait for initialization
      Office.onReady()
        .then(() => {
          isOfficeInitialized = true;
          isInitializing = false;
          initializationError = null;
          nrPageAction('officeInitialization', { message: 'Office.js initialized successfully' });
          // Run any registered callbacks
          initCallbacks.forEach(callback => {
            try {
              callback();
            } catch (callbackError) {
              console.error('Error in Office initialization callback:', callbackError);
            }
          });
          resolve(true);
        })
        .catch(error => {
          isInitializing = false;
          initializationError = error;
          nrPageAction('officeInitialization', { message: `Office.js initialization failed: ${error}` });
          reject(error);
        });
    } catch (error) {
      isInitializing = false;
      initializationError = error as Error;
      nrPageAction('officeInitialization', { message: `Office.js initialization error: ${error}` });
      reject(error);
    }
  });

  return initializationPromise;
};

/**
 * Returns a promise that resolves when Office.js is initialized
 * If Office.js initialization hasn't started, it will start it
 * @param timeoutMs Optional timeout in milliseconds
 */
export const waitForOfficeInitialization = (timeoutMs = 30000): Promise<boolean> => {
  // If already initialized, return immediately
  if (isOfficeInitialized) {
    return Promise.resolve(true);
  }

  // If there was an initialization error, return rejected promise
  if (initializationError && !isInitializing) {
    return Promise.reject(initializationError);
  }

  // If not started yet, start initialization
  if (!initializationPromise) {
    return initializeOffice();
  }

  // Add timeout if requested
  if (timeoutMs > 0) {
    return Promise.race([
      initializationPromise,
      new Promise<boolean>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Office.js initialization timed out after ${timeoutMs}ms`));
        }, timeoutMs);
      })
    ]);
  }

  // Otherwise return the existing promise
  return initializationPromise;
};

/**
 * Register a callback to be executed when Office.js is initialized
 * If Office.js is already initialized, the callback will be executed immediately
 * @param callback Function to call when Office.js is initialized
 */
export const onOfficeInitialized = (callback: () => void): void => {
  if (isOfficeInitialized) {
    // If already initialized, execute callback immediately
    try {
      callback();
    } catch (error) {
      console.error('Error in Office initialization callback:', error);
    }
  } else {
    // Otherwise add to queue
    initCallbacks.push(callback);
  }
};

/**
 * Returns whether Office.js is initialized
 */
export const isOfficeReady = (): boolean => {
  return isOfficeInitialized;
};

/**
 * Returns any error that occurred during Office.js initialization
 */
export const getOfficeInitializationError = (): Error | null => {
  return initializationError;
};
